import { useStore } from 'effector-react'
import { useMemo } from 'react'
import { promoCodeModel } from '~/features/BookingCars/BookingCar/PromoCode'
import { BookingCarDiscount } from '~/shared/api'
import { CURRENCY, OptionEnum } from '~/shared/config/constants'
import { RentalContractWorkScheduleEnum } from '~/shared/config/enums'
import { calculateDiscountPrice, priceFormat } from '~/shared/lib/utils'
import { AccordionGroup } from '~/shared/ui/AccordionGroup'
import { PriceContentDrawer } from '../PriceContentDrawer/PriceContentDrawer'
import {
  Price,
  DiscountPrice,
  OldPrice,
  IonAccordion,
  Content,
  PricesContainer,
  DiscountAmount,
  PriceRow,
  ContentRow,
  DiscountTitle,
  DiscountAmountRow,
} from './styled'

interface PriceBlockProps {
  pricePerDay: number
  discount?: BookingCarDiscount
  workSchedule: OptionEnum<RentalContractWorkScheduleEnum>
}

export function PriceBlock({
  pricePerDay,
  discount,
  workSchedule,
}: PriceBlockProps) {
  const validatedPromoCode = useStore(promoCodeModel.$validatedPromoCode)

  const price = useMemo(() => {
    const isSixThroughOne =
      workSchedule?.id === RentalContractWorkScheduleEnum.SIX_THROUGH_ONE
    const basePrice = isSixThroughOne ? (pricePerDay * 7) / 6 : pricePerDay

    const activeDiscount = validatedPromoCode?.valid
      ? validatedPromoCode
      : discount

    if (!activeDiscount) {
      return { basePrice, finalPrice: basePrice, hasDiscount: false }
    }

    return {
      basePrice,
      finalPrice: calculateDiscountPrice(basePrice, {
        details: activeDiscount.details ?? {
          discount_amount: 0,
          discount_percent: 0,
        },
      }),
      hasDiscount: true,
      discountAmount: activeDiscount.details,
      discountTitle: activeDiscount?.title,
      discountDescription: activeDiscount?.description,
    }
  }, [pricePerDay, workSchedule, validatedPromoCode, discount])

  const amount = price.discountAmount?.discount_amount

  return price.hasDiscount ? (
    <>
      <AccordionGroup>
        <IonAccordion value='price'>
          <PricesContainer slot='header'>
            <PriceRow>
              <OldPrice>
                {priceFormat(price.basePrice)} {CURRENCY}
              </OldPrice>
              <DiscountPrice>
                {priceFormat(price.finalPrice)} {CURRENCY} / сутки
              </DiscountPrice>
            </PriceRow>
          </PricesContainer>
          <Content slot='content'>
            <ContentRow>
              <DiscountTitle>{price.discountTitle}</DiscountTitle>
              <DiscountAmountRow>
                <DiscountAmount>
                  {price.hasDiscount
                    ? amount
                      ? `- ${priceFormat(amount)}`
                      : `- ${price.discountAmount?.discount_percent}%`
                    : null}
                  {CURRENCY}
                </DiscountAmount>
                <PriceContentDrawer
                  title={price.discountTitle ?? ''}
                  description={price.discountDescription ?? ''}
                />
              </DiscountAmountRow>
            </ContentRow>
          </Content>
        </IonAccordion>
      </AccordionGroup>
    </>
  ) : (
    <Price data-testid='booking-car-price'>
      {priceFormat(price.finalPrice)} {CURRENCY} / сутки
    </Price>
  )
}
